(function (global) {
  'use strict';
  const signature = items => JSON.stringify((items || []).map(i => [i.key, i.quantity]));
  function recommendationFamily(product) {
    const name = String(product.product_title || product.title || product.handle || '').toLowerCase().replace(/[-_]/g, ' ');
    // Recognize common alternatives even when Shopify assigns a bundle a different product type.
    const families = [
      ['shift-knob', /\bshift(?:er)? knobs?\b/],
      ['cable-bushing', /\b(?:shift(?:er)? )?cable bushings?\b/],
      ['shifter-bushing', /\bshifter (?:base|mount(?:ing)?) bushings?\b/],
      ['cable-clip', /\b(?:shift(?:er)? )?cable clips?\b/],
      ['shift-cable', /\bshift(?:er)? cables?\b/],
      ['shift-boot', /\bshift(?:er)? boots?\b/],
      ['shifter', /\b(?:short shifter|shifter assembly|shifter assemblies)\b/]
    ];
    for (const [family, pattern] of families) if (pattern.test(name)) return family;
    const type = String(product.product_type || product.type || '').trim().toLowerCase();
    return type && !/^(?:other|parts|accessories|drivetrain|interior|exterior|performance parts)$/.test(type) ? 'type:' + type : '';
  }
  function selectRecommendations(products, items) {
    const inCart = new Set(items.map(i => String(i.product_id)));
    const handles = new Set(items.map(i => i.handle));
    const families = new Set(items.map(recommendationFamily).filter(Boolean));
    const selected = [];
    for (const product of products) {
      const family = recommendationFamily(product) || 'unclassified';
      if (!product.available || inCart.has(String(product.id)) || handles.has(product.handle) || families.has(family)) continue;
      selected.push(product); families.add(family); handles.add(product.handle); inCart.add(String(product.id));
      if (selected.length === 2) break;
    }
    return selected;
  }
  function fitSummary(fits, groups) {
    const rows = groups.map(g => ({ ...(fits || []).find(f => f.groupId === g.id), ...g }));
    const listed = rows.filter(f => f.status === 'group-listed').length;
    if (listed) return { kind: 'listed', text: groups.length === 1 ? 'Listed for your vehicle' : `Listed for ${listed} of ${groups.length} vehicles`, rows };
    if (rows.length && rows.every(f => f.status === 'universal')) return { kind: 'universal', text: 'Universal · check requirements', rows };
    return { kind: 'review', text: 'Fitment not confirmed', rows };
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { signature, fitSummary, selectRecommendations }; return; }
  global.hbrCartDrawer = function () {
    let timer, version = 0, controller, metadataSignature = '', recommendationKey = '', candidates = [];
    let onVehicle, onGarageClose;
    return {
      busy: false, error: '', announcement: '', eligibility: {}, fitments: {}, recommendations: [],
      selection: { groups: [], includeUniversal: true }, fitState: 'idle', garageOpen: false, garageAvailable: false,
      root: '/', currency: 'USD', shippingEnabled: false, shippingThreshold: 0, shippingSuccess: 'Free shipping unlocked', affirmEnabled: false,
      init() {
        const config = this.$el.dataset;
        this.root = config.root || '/'; this.currency = config.currency || 'USD';
        this.shippingEnabled = config.shippingEnabled === 'true';
        this.shippingThreshold = Number(config.shippingThreshold) || 0;
        this.shippingSuccess = config.shippingSuccess || 'Free shipping unlocked';
        this.affirmEnabled = config.affirm === 'true';
        onVehicle = () => this.queueRefresh();
        document.addEventListener('hybrid:vehiclechange', onVehicle);
        document.addEventListener('hybrid:garagestate', onVehicle);
        this.$watch('cart.items', () => this.queueRefresh());
        this.$watch('cart.total_price', () => this.refreshAffirm());
        this.$watch('cart_drawer', open => { if (open) this.queueRefresh(); else { version++; controller?.abort(); } });
        if (this.cart_drawer) this.queueRefresh();
      },
      destroy() {
        clearTimeout(timer); controller?.abort(); version++;
        document.removeEventListener('hybrid:vehiclechange', onVehicle);
        document.removeEventListener('hybrid:garagestate', onVehicle);
        document.getElementById('hbr-garage-dialog')?.removeEventListener('close', onGarageClose);
      },
      money(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.cart.currency || this.currency }).format((Number(cents) || 0) / 100);
      },
      get threshold() { return Math.round(this.shippingThreshold * (Number(global.Shopify?.currency?.rate) || 1)); },
      get shippingGap() { return Math.max(0, this.threshold - this.cart.total_price); },
      get shippingProgress() { return this.threshold > 0 ? Math.min(100, Math.max(0, this.cart.total_price / this.threshold * 100)) : 0; },
      get eligibleCount() { return this.cart.items.reduce((count, item) => count + (this.eligibility[item.key] === true ? item.quantity : 0), 0); },
      get firstEligibleKey() { return this.cart.items.find(item => this.eligibility[item.key] === true)?.key; },
      get vehicleLabel() { return this.selection.groups.map(g => g.label).join(' + ') || 'Choose your vehicle'; },
      get browseUrl() { return global.HybridGarage?.browseUrl?.() || this.root + 'collections/all'; },
      productUrl(url) {
        if (!url || !this.garageAvailable) return url;
        const destination = new URL(url, location.origin);
        if (destination.origin !== location.origin) return url;
        // Read the reactive selection here; the shared garage's internal state is not Alpine reactive.
        destination.searchParams.set('groups', this.selection.groups.map(g => g.id).join(','));
        destination.searchParams.set('universal', String(this.selection.includeUniversal));
        return destination.pathname + destination.search + destination.hash;
      },
      properties(item) { return Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value !== '' && value != null); },
      fit(item) { return this.fitments[item.handle]; },
      fitDetails(item) {
        return (this.fit(item)?.rows || []).map(row => row.label + ': ' +
          (row.status === 'group-listed' ? 'Listed for this vehicle' : row.status === 'universal' ? 'Universal · check requirements' : 'Fitment not confirmed')).join('\n');
      },
      queueRefresh() {
        clearTimeout(timer); version++; controller?.abort();
        this.fitments = {}; this.recommendations = []; this.fitState = 'idle';
        if (metadataSignature !== signature(this.cart.items)) this.eligibility = {};
        this.selection = global.HybridGarage?.selection?.() || { groups: [], includeUniversal: true };
        this.garageAvailable = !!global.HybridGarage;
        if (this.cart_drawer) timer = setTimeout(() => this.refresh(), 40);
      },
      async read(url, options = {}) {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options,
          headers: { Accept: 'application/json', ...options.headers } });
        const body = await response.json();
        if (!response.ok) throw new Error(typeof body.description === 'string' ? body.description : 'Your cart could not be updated. Please try again.');
        return body;
      },
      parseMetadata(html) {
        if (typeof html !== 'string') return null;
        try {
          const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hbr-cart-metadata]');
          const rows = JSON.parse(node?.textContent || 'null');
          if (!Array.isArray(rows)) return null;
          return Object.fromEntries(rows.filter(row => typeof row.key === 'string').map(row => [row.key, row.eligible === true]));
        } catch (_) { return null; }
      },
      async refreshMetadata(token, signal) {
        const currentSignature = signature(this.cart.items);
        if (metadataSignature === currentSignature) return;
        try {
          const sections = await this.read(this.root + '?sections=hbr-cart-metadata', { signal });
          const data = this.parseMetadata(sections['hbr-cart-metadata']);
          if (token === version && data) { this.eligibility = data; metadataSignature = currentSignature; }
        } catch (_) { /* Optional eligibility must never interrupt cart operations. */ }
      },
      async refresh() {
        const token = ++version; controller?.abort(); controller = new AbortController();
        const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(8000)]);
        this.selection = global.HybridGarage?.selection?.() || { groups: [], includeUniversal: true };
        this.garageAvailable = !!global.HybridGarage;
        this.refreshAffirm();
        const metadata = this.refreshMetadata(token, signal);
        if (!this.selection.groups.length || !this.cart.items.length || !global.HybridGarage?.checkProducts) { await metadata; return; }
        this.fitState = 'loading';
        const groups = this.selection.groups, ids = groups.map(g => g.id);
        try {
          const handles = [...new Set(this.cart.items.map(i => i.handle).filter(Boolean))];
          const products = [];
          for (let offset = 0; offset < handles.length; offset += 24) {
            const data = await global.HybridGarage.checkProducts(handles.slice(offset, offset + 24), ids, signal);
            products.push(...(data.products || []));
          }
          if (token !== version) return;
          this.fitments = Object.fromEntries(handles.map(handle => [handle, fitSummary(products.find(p => p.handle === handle)?.fits, groups)]));
          this.fitState = 'ready';
          await this.loadRecommendations(token, ids, signal);
        } catch (_) { if (token === version) { this.fitState = 'unavailable'; this.fitments = {}; this.recommendations = []; } }
        finally { await metadata; }
      },
      async loadRecommendations(token, ids, signal) {
        const anchors = [...new Set([...this.cart.items].sort((a, b) => (b.price || 0) - (a.price || 0)).map(i => i.product_id).filter(Boolean))].slice(0, 3);
        if (!anchors.length) return;
        try {
          const key = anchors.join(',') + ':' + (this.cart.currency || this.currency);
          if (key !== recommendationKey) {
            const results = await Promise.allSettled(anchors.map(anchor => this.read(this.root + 'recommendations/products.json?product_id=' + encodeURIComponent(anchor) + '&limit=10&intent=complementary', { signal })));
            if (token !== version) return;
            const lists = results.map(result => result.status === 'fulfilled' ? (result.value.products || []).slice(0, 10) : []);
            const seen = new Set(); candidates = [];
            // Interleave Shopify's ranked lists so a second cart item gets a chance to contribute.
            for (let index = 0; index < 10; index++) for (const list of lists) {
              const product = list[index];
              if (product?.available && product.handle && !seen.has(product.handle)) { candidates.push(product); seen.add(product.handle); }
            }
            recommendationKey = results.every(result => result.status === 'fulfilled') ? key : '';
          }
          const cartFamilies = new Set(this.cart.items.map(recommendationFamily).filter(Boolean));
          const available = candidates.filter(p => !this.cart.items.some(i => String(i.product_id) === String(p.id) || i.handle === p.handle) && !cartFamilies.has(recommendationFamily(p)));
          if (!available.length) { this.recommendations = []; return; }
          const products = [];
          for (let offset = 0; offset < available.length; offset += 24) {
            const data = await global.HybridGarage.checkProducts(available.slice(offset, offset + 24).map(p => p.handle), ids, signal);
            products.push(...(data.products || []));
          }
          if (token !== version) return;
          this.recommendations = selectRecommendations(available.filter(p => {
            const fits = products.find(f => f.handle === p.handle)?.fits || [];
            return fits.some(f => ids.includes(f.groupId) && (f.status === 'group-listed' || (this.selection.includeUniversal && f.status === 'universal')));
          }), this.cart.items);
        } catch (_) { if (token === version) this.recommendations = []; }
      },
      canQuickAdd(product) { return product.variants?.length === 1 && product.variants[0].available && !product.requires_selling_plan; },
      applyCart(data) {
        if (!Array.isArray(data.items)) throw new Error('Your cart could not be refreshed. Please reload the page.');
        const metadata = this.parseMetadata(data.sections?.['hbr-cart-metadata']);
        if (metadata) { metadataSignature = signature(data.items); this.eligibility = metadata; }
        else { metadataSignature = ''; this.eligibility = {}; }
        this.cart = { ...this.cart, ...data };
      },
      async mutate(endpoint, payload, message) {
        if (this.busy || this.cart_loading) return;
        const focused = document.activeElement;
        this.busy = true; this.error = ''; this.announcement = 'Updating cart…';
        version++; controller?.abort();
        try {
          // No automatic write retry: a lost response can still mean Shopify accepted the change.
          const data = await this.read(this.root + 'cart/' + endpoint + '.js', { method: 'POST',
            signal: AbortSignal.timeout(15000), headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, sections: ['hbr-cart-metadata'], sections_url: this.root }) });
          this.applyCart(endpoint === 'add' ? await this.read(this.root + 'cart.js', { signal: AbortSignal.timeout(8000) }) : data);
          this.announcement = message;
        } catch (error) {
          this.error = error.message; this.announcement = '';
          // Reconcile the displayed cart without replaying an uncertain write.
          try { this.applyCart(await this.read(this.root + 'cart.js', { signal: AbortSignal.timeout(8000) })); } catch (_) {}
        } finally {
          this.busy = false; this.queueRefresh();
          this.$nextTick(() => {
            if (this.cart_drawer && !this.garageOpen && document.activeElement === document.body) {
              if (focused?.isConnected && !focused.disabled) focused.focus();
              else this.$el.querySelector('.hbr-cart__close')?.focus();
            }
          });
        }
      },
      changeLine(item, quantity) {
        if (!Number.isSafeInteger(quantity) || quantity < 0) return;
        return this.mutate('change', { id: item.key, quantity }, quantity ? 'Quantity updated.' : 'Item removed.');
      },
      addRecommendation(product) { if (this.canQuickAdd(product)) return this.mutate('add', { items: [{ id: product.variants[0].id, quantity: 1 }] }, 'Item added to your cart.'); },
      saveNote(value) { return this.mutate('update', { note: value }, 'Order note saved.'); },
      changeVehicle(trigger) {
        const dialog = document.getElementById('hbr-garage-dialog');
        if (!global.HybridGarage || !dialog) return;
        this.garageOpen = true;
        onGarageClose = () => { this.garageOpen = false; this.queueRefresh(); this.$nextTick(() => trigger.focus()); };
        dialog.addEventListener('close', onGarageClose, { once: true });
        this.$nextTick(() => global.HybridGarage.open(trigger, false, true));
      },
      refreshAffirm() {
        if (this.affirmEnabled) this.$nextTick(() => { try { global.affirm?.ui?.refresh?.(); } catch (_) {} });
      }
    };
  };
})(typeof window !== 'undefined' ? window : globalThis);
